import { ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import {
  ISnMapResponse,
  ISnMapQueryParams,
  ISnListResponse,
  ISnListQueryParameters,
  ISnFeedQueryParameters,
  transformListResponse,
} from '@lib/api';
import { SnCamera, SnLocation, SnLocationType, SnShare } from '@lib/api/entities';
import { ISnCanShare } from '@lib/share';
import { t } from '@lib/translation';
import { SnDialogService } from '@lib/ui/dialog';
@Injectable({ providedIn: 'root' })
export class SnLocationsAPI implements ISnCanShare {
  constructor(
    private readonly _httpClient: HttpClient,
    private readonly _toastrService: ToastrService,
    private readonly _dialogService: SnDialogService,
  ) {}

  public getLocationList(params?: ISnListQueryParameters): Observable<ISnListResponse<SnLocation>> {
    return this._httpClient.get('/api/locations/', { params }).pipe(
      map((response: any) =>
        transformListResponse(response, (item) => new SnLocation(item), {
          ordering: response.ordering,
        }),
      ),
    );
  }

  public getRegistryLocations(params?: ISnListQueryParameters): Observable<any> {
    return this._httpClient.get('/api/locations/', { params });
  }

  public getLocation(id: string): Observable<SnLocation> {
    return this._httpClient
      .get(`/api/locations/${id}/`)
      .pipe(map((response: any) => new SnLocation(response)));
  }

  public createLocation(data: any): Observable<SnLocation> {
    return this._httpClient.post('/api/locations/', data).pipe(
      map((response: any) => new SnLocation(response)),
      tap({
        next: () => this._toastrService.success(t('locations.success.created')),
        error: () => this._toastrService.error(t('locations.errors.createFailed')),
      }),
    );
  }

  public downloadBlobFile(): Observable<any> {
    return this._httpClient
      .get('/api/locations/import/sample/', {
        headers: {
          'Content-Type': 'text/csv',
        },
        responseType: 'blob',
      })
      .pipe(
        tap({
          next: (res) => {
            const downloadLink = document.createElement('a');
            downloadLink.setAttribute('download', 'locations-ex.csv');
            const url = URL.createObjectURL(res);
            downloadLink.setAttribute('href', url);
            downloadLink.click();
          },
          error: () => {
            this._toastrService.error(t('common.downloadFailed'));
          },
        }),
      );
  }

  public updateLocation(id: string, data: any): Observable<SnLocation> {
    return this._httpClient.patch(`/api/locations/${id}/`, data).pipe(
      map((response: any) => new SnLocation(response)),
      tap({
        next: () => this._toastrService.success(t('locations.success.updated')),
        error: () => this._toastrService.error(t('locations.errors.updateFailed')),
      }),
    );
  }

  public createRegistryLocation(data: Object): Observable<SnLocation> {
    const formData = this._transformToFormData(data);
    return this._httpClient.post('/api/locations/', formData).pipe(
      map((response: any) => new SnLocation(response)),
      tap({
        next: () => this._toastrService.success(t('locations.success.created')),
        error: () => this._toastrService.error(t('locations.errors.createFailed')),
      }),
    );
  }

  public updateRegistryLocation(id: string, data: Object): Observable<SnLocation> {
    const formData = this._transformToFormData(data);
    return this._httpClient.patch(`/api/locations/${id}/`, formData).pipe(
      map((response: any) => new SnLocation(response)),
      tap({
        next: () => this._toastrService.success(t('locations.success.updated')),
        error: () => this._toastrService.error(t('locations.errors.updateFailed')),
      }),
    );
  }

  public deleteLocation(id: string): Observable<Object> {
    return this._dialogService
      .confirm({
        message: t('locations.confirmations.deleteLocation'),
        confirmButton: {
          text: t('common.delete').toUpperCase(),
          color: 'warn',
        },
      })
      .pipe(
        switchMap(() => this._httpClient.delete(`/api/locations/${id}/`)),
        tap({
          next: () => this._toastrService.success(t('locations.success.deleted')),
          error: () => this._toastrService.error(t('locations.errors.deleteFailed')),
        }),
      );
  }

  public getLocationCameraList(
    id: string,
    params: ISnListQueryParameters,
  ): Observable<ISnListResponse<SnCamera>> {
    return this._httpClient.get(`/api/locations/${id}/cameras/`, { params }).pipe(
      map((response: any) => ({
        count: response.count,
        totalPages: response.totalPages,
        results: response.results,
      })),
    );
  }

  public getLocationTypeList(
    params: ISnListQueryParameters,
  ): Observable<ISnListResponse<SnLocationType>> {
    params.ordering = 'name';
    return this._httpClient.get('/api/locations/types/', { params }).pipe(
      map((response: any) => ({
        count: response.count,
        totalPages: response.totalPages,
        results: response.results,
      })),
    );
  }

  public getShareList(
    locationId: string,
    params?: ISnListQueryParameters,
  ): Observable<ISnListResponse<SnShare>> {
    return this._httpClient.get(`/api/locations/${locationId}/shares/`, { params }).pipe(
      map((response: any) => ({
        count: response.count,
        totalPages: response.totalPages,
        results: response.results.map((share) => new SnShare(share)),
      })),
    );
  }

  public createShare(locationId: string, data: any): Observable<SnShare | SnShare[]> {
    return this._httpClient.post(`/api/locations/${locationId}/shares/`, data).pipe(
      map((response: any) => {
        return Array.isArray(response)
          ? response.map((share) => new SnShare(share))
          : new SnShare(response);
      }),
    );
  }

  public updateShare(locationId: string, shareId: string, data: any): Observable<SnShare> {
    return this._httpClient
      .patch(`/api/locations/${locationId}/shares/${shareId}/`, data)
      .pipe(map((response: any) => new SnShare(response)));
  }

  public deleteShare(locationId: string, shareId: string): Observable<Object> {
    return this._dialogService
      .confirm({
        message: t('locations.confirmations.deleteShare'),
        confirmButton: {
          text: t('common.delete').toUpperCase(),
          color: 'warn',
        },
      })
      .pipe(
        switchMap(() => {
          return this._httpClient.delete(`/api/locations/${locationId}/shares/${shareId}/`);
        }),
      );
  }

  public feedLocations(params: ISnFeedQueryParameters): Observable<SnLocation[]> {
    return this._httpClient.get('/api/locations/feed/', { params }).pipe(
      map((response: any) => {
        return response.map((locations) => new SnLocation(locations));
      }),
    );
  }

  public getLocationMap(
    params: ISnListQueryParameters | ISnMapQueryParams,
  ): Observable<ISnMapResponse<SnLocation>> {
    return this._httpClient.get('/api/maps/static/locations/', { params }).pipe(
      map((response: any) => {
        return {
          results: response.results.map((location) => new SnLocation(location)),
          clusterResults: response.clusterResults || [],
        };
      }),
    );
  }

  public deleteMedia(locationId: string, itemId: string): Observable<Object> {
    return this._dialogService
      .confirm({
        message: t('locations.confirmations.deleteMedia'),
        confirmButton: {
          text: t('common.delete').toUpperCase(),
          color: 'warn',
        },
      })
      .pipe(
        switchMap(() => this._httpClient.delete(`/api/locations/${locationId}/media/${itemId}/`)),
        tap({
          next: () => this._toastrService.success(t('locations.success.mediaDeleted')),
          error: () => this._toastrService.error(t('locations.errors.deleteMediaFailed')),
        }),
      );
  }

  public getNearestLocations(lon: number, lat: number, search?: string): Observable<SnLocation[]> {
    return this._httpClient
      .get(`/api/locations/?distance_point=${lon},${lat}`, {
        params: {
          ordering: 'distance',
          pageSize: '1',
          page: '1',
          search,
        },
      })
      .pipe(
        map((response: any) => {
          return response.results.map((location) => new SnLocation(location));
        }),
      );
  }

  public importCSVFile(data: { description: string; filename: File }): Observable<any> {
    const formData = new FormData();
    formData.append('description', data.description);
    formData.append('file', data.filename);

    return this._httpClient.post('/api/locations/import/', formData).pipe(
      tap({
        next: () => {
          this._toastrService.success(t('locations.success.csvImported'));
        },
        error: () => {},
      }),
    );
  }

  private _transformToFormData(data: Object): FormData {
    const formData = new FormData();
    Object.keys(data).forEach((key) => {
      switch (key) {
        case 'geometry':
          formData.append('lon', data[key].coordinates[0]);
          formData.append('lat', data[key].coordinates[1]);
          break;
        case 'files':
          data[key].forEach((file) => {
            formData.append('files', file, file.name);
          });
          break;
        default:
          formData.append(key, data[key]);
      }
    });

    return formData;
  }

  public getImportSample(type: string): Observable<Blob> {
    return this._httpClient
      .get('/api/data/imports/sample/', {
        params: {
          choice: type,
        },
        responseType: 'blob',
      })
      .pipe(
        tap({
          next: (res) => {
            const downloadLink = document.createElement('a');
            downloadLink.setAttribute('download', `${type}-ex.csv`);
            const url = URL.createObjectURL(res);
            downloadLink.setAttribute('href', url);
            downloadLink.click();
          },
          error: () => {
            this._toastrService.error(t('common.downloadFailed'));
          },
        }),
      );
  }
}
